<script setup>
// Pricing v2: pages-denominated quota with credit awareness. When credits
// exist, the bar shows a second sky-colored segment for available credits
// and 100% plan usage reads as "Using credits" (info) instead of danger.
import { computed } from 'vue'
import { TrendingUp, RefreshCw, Coins } from 'lucide-vue-next'
import { useQuotaStore } from '@/stores/quotaStore'
import { usePlanStore } from '@/stores/planStore'
import { formatDate } from '@/utils/format'
import PlanBadge from './PlanBadge.vue'

const props = defineProps({
  variant: { type: String, default: 'card' }, // 'card' | 'inline' | 'compact'
})
const emit = defineEmits(['upgrade', 'buy-pages'])

const quota = useQuotaStore()
const plan = usePlanStore()

const hasOverage = computed(() => quota.isUsingOverage)

const barClass = computed(() => {
  if (quota.percentUsed >= 100 && quota.overageEnabled) return 'bg-secondary'
  if (quota.percentUsed >= 90) return 'bg-rose-500'
  if (quota.percentUsed >= 70) return 'bg-amber-500'
  return 'bg-primary'
})

const trackClass = computed(() => {
  if (quota.percentUsed >= 100 && quota.overageEnabled) return 'bg-sky-100 dark:bg-sky-950/40'
  if (quota.percentUsed >= 90) return 'bg-rose-100 dark:bg-rose-950/40'
  if (quota.percentUsed >= 70) return 'bg-amber-100 dark:bg-amber-950/40'
  return 'bg-slate-100 dark:bg-slate-800'
})

const planFillPct = computed(() => Math.min(100, (quota.pagesUsed / (quota.pagesLimit || 1)) * 100))

const summaryLine = computed(() => {
  const base = `${quota.pagesUsed.toLocaleString()} / ${quota.pagesLimit.toLocaleString()} pages`
  return hasOverage.value ? `${base} · $${(quota.overageSpendCents / 100).toFixed(2)} overage` : base
})

const showUpgradeCta = computed(() => quota.isNearLimit && plan.tier !== 'enterprise')
</script>

<template>
  <!-- Compact (sidebar) -->
  <div v-if="variant === 'compact'" class="space-y-1">
    <div class="flex items-center justify-between text-xs text-slate-500 dark:text-slate-400">
      <span>{{ summaryLine }}</span>
      <PlanBadge :clickable="false" />
    </div>
    <div class="h-1.5 overflow-hidden rounded-full" :class="trackClass">
      <div class="h-full transition-all" :class="barClass" :style="{ width: `${Math.max(planFillPct, 2)}%` }" />
    </div>
  </div>

  <!-- Inline (header strip) -->
  <div v-else-if="variant === 'inline'" class="flex flex-wrap items-center gap-3 text-sm">
    <PlanBadge :clickable="false" />
    <div class="min-w-[160px] flex-1">
      <div class="h-1.5 overflow-hidden rounded-full" :class="trackClass">
        <div class="h-full transition-all" :class="barClass" :style="{ width: `${Math.max(planFillPct, 2)}%` }" />
      </div>
    </div>
    <span class="text-xs text-slate-500 dark:text-slate-400">{{ summaryLine }}</span>
    <button v-if="showUpgradeCta" type="button" class="text-xs font-medium text-primary hover:underline" @click="emit('upgrade')">
      Upgrade →
    </button>
  </div>

  <!-- Card (dashboard / settings) -->
  <div v-else class="card-surface p-6">
    <div class="flex items-start justify-between gap-3">
      <div>
        <h3 class="font-display text-lg font-semibold">Monthly pages</h3>
        <p class="mt-1 text-sm text-slate-600 dark:text-slate-400">
          {{ quota.pagesUsed.toLocaleString() }} of {{ quota.pagesLimit.toLocaleString() }} plan pages used this period.
        </p>
      </div>
      <PlanBadge size="md" :clickable="true" @click="emit('upgrade')" />
    </div>

    <div class="mt-5 h-2.5 overflow-hidden rounded-full" :class="trackClass">
      <div
        class="h-full rounded-full transition-all duration-500"
        :class="barClass"
        :style="{ width: `${Math.max(planFillPct, 2)}%` }"
      />
    </div>

    <div class="mt-3 flex flex-wrap items-center justify-between gap-2 text-xs text-slate-500 dark:text-slate-400">
      <span class="inline-flex items-center gap-1">
        <RefreshCw class="h-3 w-3" />
        Plan pages reset {{ formatDate(quota.periodResetDate, { month: 'long', day: 'numeric' }) }}
      </span>
      <span v-if="hasOverage" class="inline-flex items-center gap-1">
        <Coins class="h-3 w-3" />
        {{ quota.overagePagesPeriod.toLocaleString() }} overage pages · ${{ (quota.overageSpendCents / 100).toFixed(2) }} this cycle
      </span>
      <span v-else>{{ quota.remainingPlanPages.toLocaleString() }} remaining</span>
    </div>

    <div v-if="quota.budgetAlert" class="mt-3 rounded-lg bg-amber-50 px-3 py-2 text-xs text-amber-800 dark:bg-amber-950/30 dark:text-amber-300">
      You've used {{ quota.alertThresholdPct }}%+ of your ${{ (quota.overageLimitCents / 100).toFixed(2) }} overage budget this cycle.
    </div>
    <div v-else-if="quota.isUsingOverage" class="mt-3 rounded-lg bg-sky-50 px-3 py-2 text-xs text-sky-800 dark:bg-sky-950/30 dark:text-sky-300">
      Plan pages are used up — extra pages bill at {{ quota.overageRate }}¢/page within your budget.
    </div>

    <button v-if="showUpgradeCta" type="button" class="btn-primary mt-4 w-full" @click="emit('upgrade')">
      <TrendingUp class="h-4 w-4" />
      <span v-if="quota.isExhausted">Out of pages — upgrade for more</span>
      <span v-else>Approaching limit — see options</span>
    </button>
  </div>
</template>
